import {computed, ref, watch, onUnmounted} from "ui.vue3";

export default {
  name: 'VfnModal',
  props: {
    modelValue: Boolean,
    modifiers: {
      type: [String, Object, Array],
      default: ''
    },
    contentsHeight: {
      type: String,
      default: '97vh',
    },
    dynamicHeight: {
      type: Boolean,
      default: false
    },
  },
  emits: ['update:modelValue', 'afterLeave', 'closeModal'],
  setup (props, context) {
    const isDragging = ref(false)
    const modalHeight = ref(0)
    const contentsBottomPosition = ref('0px')
    const dragStartCoordinateY = ref(0)
    const currentDragCoordinateY = ref(0)
    
    const root = ref(null)
    const modalContents = ref(null)
    const scrollContents = ref(null)
    
    const SWIPE_CLOSE_THRESHOLD_FACTOR = 0.125;
    
    const modal = computed({
      get: () => props.modelValue,
      set: (value) => context.emit('update:modelValue', value),
    })
    
    watch(() => props.modelValue, (newModalValue) => {
      if (newModalValue) {
        initializeModal();
      } else {
        resetInteractionState();
      }
    })
    
    const setRootCssVariable = (variableName, value) => {
      if (root.value) {
        root.value.style.setProperty(variableName, value);
      }
    }
    
    const resetInteractionState = () => {
      isDragging.value = false;
      dragStartCoordinateY.value = 0;
      currentDragCoordinateY.value = 0;
    }
    
    const initializeModal = () => {
      resetInteractionState();
      modalHeight.value = 0;
      contentsBottomPosition.value = '0px';
      
      setRootCssVariable('--contents-height', props.contentsHeight);
      setRootCssVariable('--contents-bottom-position', '0px');
    }
    
    const closeModal = () => {
      context.emit('update:modelValue', false);
      context.emit('closeModal');
      resetInteractionState();
    }
    
    const onAfterLeave = () => {
      window.scrollLock.enablePageScroll()
      context.emit('afterLeave');
    }
    
    const handleDragStart = (pageY) => {
      if (!modalContents.value) return;
      modalHeight.value = modalContents.value.getBoundingClientRect().height;
      dragStartCoordinateY.value = pageY;
      currentDragCoordinateY.value = pageY;
      isDragging.value = true;
    }
    
    const handleDragMove = (pageY) => {
      if (!isDragging.value) return;
      
      currentDragCoordinateY.value = pageY;
      const dragDelta = dragStartCoordinateY.value - currentDragCoordinateY.value;
      
      contentsBottomPosition.value = (dragDelta <= 0 ? dragDelta : 0) + 'px';
      setRootCssVariable('--contents-bottom-position', contentsBottomPosition.value);
    }
    
    const handleDragEnd = () => {
      if (!isDragging.value) return;
      
      const totalVerticalDrag = currentDragCoordinateY.value - dragStartCoordinateY.value;
      const wasDragging = isDragging.value;
      
      resetInteractionState();
      
      if (wasDragging && modalHeight.value > 0 && totalVerticalDrag > (modalHeight.value * SWIPE_CLOSE_THRESHOLD_FACTOR)) {
        closeModal();
      } else if (wasDragging) {
        contentsBottomPosition.value = '0px';
        setRootCssVariable('--contents-bottom-position', '0px');
      }
    }
    
    const onMouseDown = (event) => {
      if (event.button !== 0) return;
      handleDragStart(event.pageY);
    }
    const onMouseMove = (event) => {
      if(isDragging.value) {
        handleDragMove(event.pageY);
      }
    }
    const onMouseUp = () => {
      if(isDragging.value) {
        handleDragEnd();
      }
    }
    
    const onTouchStart = (event) => {
      if (scrollContents.value && scrollContents.value.scrollTop <= 0) {
        handleDragStart(event.touches[0].pageY);
      }
    }
    const onTouchMove = (event) => {
      if(isDragging.value) {
        handleDragMove(event.touches[0].pageY);
      }
    }
    const onTouchEnd = () => {
      if(isDragging.value) {
        handleDragEnd();
      }
    }
    
    const onWrapperEnter = () => {
      window.scrollLock.disablePageScroll()
    }
    
    return {
      root,
      modalContents,
      scrollContents,
      
      modal,
      
      closeModal,
      
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onAfterLeave,
      onWrapperEnter
    }
  },
  template: `
    <teleport to="body">
      <div class="vfn-modal" :class="modifiers" ref="root" @mousedown="onMouseDown" @mousemove="onMouseMove" @mouseup="onMouseUp" @mouseleave="onMouseUp">
  
        <transition appear @after-leave="onAfterLeave">
          <div v-if="modelValue"
               class="vfn-modal__bg"
               @click="closeModal">
          </div>
        </transition>
  
        <transition appear @enter="onWrapperEnter">
          <div v-if="modelValue"
               class="vfn-modal__wrapper"
               ref="modalContents"
               @touchstart.passive="onTouchStart"
               @touchmove.passive="onTouchMove"
               @touchend.passive="onTouchEnd"
               @touchcancel.passive="onTouchEnd"
               data-scroll-lock-fill-gap>
            <div class="vfn-modal__inner">
  
              <div class="vfn-modal__content" data-scroll-lock-scrollable ref="scrollContents">
  
                <div class="vfn-modal__x" @click="closeModal">
                  <img src="/local/templates/new_site_v2/images/parthners_map/close_modal.svg">
                </div>
  
                <slot />
  
              </div>
  
            </div>
          </div>
        </transition>
  
      </div>
    </teleport>
  `,
}
